/**
 * demand-window — the six-month window on an expanded Demand (Demands page, spec R6/R7,
 * AC6/AC7).
 *
 * The window opens with the CALENDAR current month as its last column and the five months
 * before it to its left — anchored on the calendar, not NOS's `CurrentPeriod`, which can
 * lag (same decision as the Resource guidance matrix). It steps one month at a time,
 * bounded so it never moves wholly past the Demand's data: the earliest start shows the
 * Demand's first month with data in its first column, the latest start shows its last
 * month (or the current month, whichever is later) in its last column.
 *
 * Pure and clock-free: callers pass `now`. Months are `YYYY-MM` strings throughout.
 */
import { monthRange } from "@/lib/plan-window";

export const DEMAND_WINDOW_MONTHS = 6;

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** Months since year 0 for a `YYYY-MM`, or null when malformed. */
function ordinal(month: string): number | null {
  const m = MONTH.exec(month);
  if (!m) return null;
  return Number(m[1]) * 12 + Number(m[2]) - 1;
}

function fromOrdinal(n: number): string {
  return `${String(Math.floor(n / 12)).padStart(4, "0")}-${String((n % 12) + 1).padStart(2, "0")}`;
}

export function isMonthKey(value: string): boolean {
  return ordinal(value) !== null;
}

/** The UTC calendar month of `now`, `YYYY-MM`. */
export function currentMonth(now: Date): string {
  return fromOrdinal(now.getUTCFullYear() * 12 + now.getUTCMonth());
}

/** `month` moved by `delta` months (negative = earlier). A malformed month is returned as-is. */
export function shiftMonth(month: string, delta: number): string {
  const n = ordinal(month);
  return n === null ? month : fromOrdinal(n + delta);
}

/** Months `a` − `b` (positive when `a` is later); 0 when either is malformed. */
function diff(a: string, b: string): number {
  const x = ordinal(a);
  const y = ordinal(b);
  return x === null || y === null ? 0 : x - y;
}

/** Where the window opens: the current month in the last column. */
export function defaultWindowStart(now: Date): string {
  return shiftMonth(currentMonth(now), -(DEMAND_WINDOW_MONTHS - 1));
}

/** The first and last month a Demand has guidance or actuals in, or null when it has none. */
export interface DemandSpan {
  first: string;
  last: string;
}

export interface WindowBounds {
  minStart: string;
  maxStart: string;
}

/**
 * The range a window start may move within. Always includes the default window, so a
 * Demand with no data (or data only inside the default window) gets no arrows at all —
 * paging only applies once there are more than six months to show (AC7).
 */
export function windowBounds(span: DemandSpan | null, now: Date): WindowBounds {
  const defaultStart = defaultWindowStart(now);
  const current = currentMonth(now);
  if (span === null) return { minStart: defaultStart, maxStart: defaultStart };
  const minStart = diff(span.first, defaultStart) < 0 ? span.first : defaultStart;
  const lastShown = diff(span.last, current) > 0 ? span.last : current;
  return { minStart, maxStart: shiftMonth(lastShown, -(DEMAND_WINDOW_MONTHS - 1)) };
}

/** `start` pulled inside `bounds`; a malformed start falls back to the default window. */
export function clampWindowStart(start: string, bounds: WindowBounds, now: Date): string {
  if (!isMonthKey(start)) return defaultWindowStart(now);
  if (diff(start, bounds.minStart) < 0) return bounds.minStart;
  if (diff(start, bounds.maxStart) > 0) return bounds.maxStart;
  return start;
}

export function canStepBack(start: string, bounds: WindowBounds): boolean {
  return diff(start, bounds.minStart) > 0;
}

export function canStepForward(start: string, bounds: WindowBounds): boolean {
  return diff(start, bounds.maxStart) < 0;
}

/** The six `YYYY-MM` months a window starting at `start` shows, oldest first. */
export function windowMonths(start: string): string[] {
  return monthRange(start, DEMAND_WINDOW_MONTHS);
}

/**
 * A window start that puts `month` in the last column — used by "Jump to last activity"
 * when the visible window holds no data (for example a Demand completed long ago).
 */
export function windowEndingAt(month: string): string {
  return shiftMonth(month, -(DEMAND_WINDOW_MONTHS - 1));
}
